// @givmo/app-charity — the claim funnel's pre-approval Charity Terms read, addressed by URL (CPA-WEB12).
//
// ══ WHY THIS FILE IS HAND-WRITTEN ══════════════════════════════════════════════════════════════
//
// `GET /charity-admin/charities/{charity_id}/claim/terms` (CPA-BE18, backend PR #2153) landed on a
// backend branch that has NOT merged to `development`, so the committed OpenAPI snapshot carries no
// operation, no query factory and no path for it. `agent_docs/api-client.md` § *A whole ROUTE the
// snapshot lacks* is the sanctioned disposition and it rules out both alternatives explicitly: do not
// hand-edit `src/generated/**` (the drift gate), and do not regen from an unlanded spec. A refresh
// would also absorb every unrelated backend change since the last regen for the sake of one endpoint.
//
// `givmoClient` is the same `@hey-api/client-fetch` client every generated operation binds to, so this
// request inherits the whole interceptor stack — Bearer, App Check, `X-Request-Id` (`ch-`), the
// `{data,meta,links}` envelope unwrap, and the `{errors:[…]}` → `GivmoError` mapping. Nothing about it
// differs from a generated call except who wrote the URL.
//
// ══ WHY THIS READ EXISTS AT ALL ════════════════════════════════════════════════════════════════
//
// The only other read of this document is `GET /charity-admin/charities/{id}/terms`, which requires a
// charity membership — and membership is created BY claim approval. So a claimant could not read the
// agreement they are being asked to accept. This route is the same document, served to a caller who is
// SIGNED IN with no membership: same 200 body (`CharityTermsOut`), byte-identical projection on the
// server (one `_current_terms_projection`, two doors), no feature flag, no membership.
//
// Refusals, from the backend's own map: `403` not signed in · `400` no such charity · `404` no
// effective Charity Terms version · `422` malformed `charity_id`.
//
// ══ GRADUATION, WHEN A REGEN ABSORBS THE ROUTE ═════════════════════════════════════════════════
//
// This module and its URL test go — the URL and cache key are generated then, and a test of
// URL-building this repo no longer does proves nothing. Two things do NOT go with them:
//
//   • `claim-terms-decode.ts` STAYS, moved into the query's `select`. The generated type would
//     describe the wire, not what arrived, and the three fields this screen pins (`version_number`,
//     `content_hash`, `content_markdown`) are exactly the ones a collapsed decode would let through
//     as `undefined` — into an acceptance record.
//   • ⚠ The swap moves this query INTO family-invalidation reach: the hand-written key below carries
//     no `_id`, so a `createQueryKey`-prefix predicate never matches it, while a generated key does.
//     Nothing in the claim funnel invalidates the terms family today; decide deliberately rather than
//     assuming the graduation is behaviour-neutral.
//   • ⚠ And it silently ADDS cancellation: the generated `*Options` factories forward the query's
//     `signal` into the operation, and `getClaimTerms` does not, so today this one read runs to
//     completion after an unmount. Harmless on a screen the claimant is sitting on — but it is a
//     second behaviour the graduation changes without anyone asking for it, so note it then too.

import { givmoClient } from '@givmo/api-client';

/**
 * The pre-approval read's URL.
 *
 * `encodeURIComponent` on the id not because a UUID needs escaping, but because the day a charity id
 * stops being a UUID is not the day anybody re-reads this file.
 */
export function claimTermsUrl(charityId: string): string {
  return `/charity-admin/charities/${encodeURIComponent(charityId)}/claim/terms`;
}

/**
 * The cache key. Hand-written, so it carries no `_id` and no generated family matches it — see the
 * graduation note above.
 *
 * Keyed by charity id: a claimant may walk back to search and open a DIFFERENT organization's wizard
 * inside one session, and the token substitutions in the body ({legal name} / {ein} /
 * {registered domain}) are resolved from the charity row, so two organizations do not share a
 * document even at the same version.
 */
export function claimTermsKey(charityId: string): readonly unknown[] {
  return ['claim-terms', charityId];
}

/** The current effective Charity Terms for this charity, for a signed-in caller with no membership. */
export async function getClaimTerms(charityId: string): Promise<unknown> {
  const { data } = await givmoClient.get({
    url: claimTermsUrl(charityId),
    throwOnError: true,
  });
  return data;
}
